import React, { useEffect, useState, useCallback } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import DataTable from "../../components/DataTable/DataTable";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { Routes, Route } from "react-router-dom";

const fieldSx = { "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } };

function FormRow({ label, children, alignStart }) {
  return (
    <div
      className={`grid grid-cols-[140px_1fr] gap-2 py-px ${alignStart ? "items-start" : "items-center"}`}
    >
      <span className={`text-xs text-gray-600 whitespace-nowrap ${alignStart ? "pt-2" : ""}`}>
        {label}
      </span>
      {children}
    </div>
  );
}

export default function ComplianceTypes() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const SESSION_PAGE_SIZE_KEY = "compliance_ui_page_size";
  const defaultSize = (() => {
    try {
      const s = sessionStorage.getItem(SESSION_PAGE_SIZE_KEY);
      const n = Number(s);
      if (n) return n;
    } catch (e) {}
    return 20;
  })();
  const [size, setSize] = useState(defaultSize);
  const [total, setTotal] = useState(0);
  const [activeFilters, setActiveFilters] = useState({});
  const [lastLoadFailed, setLastLoadFailed] = useState(false);

  const load = useCallback(
    async (p = page, s = size, filters = activeFilters) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", p);
        params.set("size", s);
        Object.entries(filters || {}).forEach(([k, v]) => {
          params.set(`filter.${k}`, v);
        });
        const data = await apiGet(`/api/compliance-types?${params.toString()}`);
        const content = Array.isArray(data) ? data : data.content || [];
        setList(content);
        setTotal(data.totalElements || content.length || 0);
      } catch (err) {
        console.error(err);
        alert("Failed to load compliance types: " + err.message);
      } finally {
        setLoading(false);
      }
    },
    [page, size, activeFilters],
  );

  const handleFiltersChange = React.useCallback(
    (filters) => {
      if (lastLoadFailed) return;
      setActiveFilters((prev) => {
        const prevKeys = Object.keys(prev || {});
        const nextKeys = Object.keys(filters || {});
        if (
          prevKeys.length === nextKeys.length &&
          nextKeys.every(
            (k) => String(prev[k] || "") === String(filters[k] || ""),
          )
        ) {
          return prev;
        }
        return filters;
      });
      setPage(0);
      load(0, size, filters || {});
    },
    [lastLoadFailed, size, load],
  );

  useEffect(() => {
    load();
  }, []);

  const handlePageSizeChange = React.useCallback(
    (newSize) => {
      try {
        sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, String(newSize));
      } catch (e) {}
      setSize(newSize);
      setPage(0);
    },
    [load, activeFilters],
  );

  const handlePageChange = React.useCallback(
    (newPage) => {
      setPage(newPage);
    },
    [load, size, activeFilters],
  );

  const [addOpen, setAddOpen] = useState(false);
  const [newType, setNewType] = useState("");
  const [editingItem, setEditingItem] = useState(null);

  function openAddDialog() {
    setNewType("");
    setEditingItem(null);
    setAddOpen(true);
  }

  function openEditDialog(item) {
    setEditingItem(item);
    setNewType(item.type || "");
    setAddOpen(true);
  }

  async function handleDelete(item) {
    if (!window.confirm("Delete compliance type " + item.type + "?")) return;
    try {
      await apiDelete(`/api/compliance-types/${item.id}`);
      load();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  }

  async function handleSave() {
    if (!newType || newType.trim() === "") {
      alert("Type is required");
      return;
    }
    try {
      if (editingItem) {
        await apiPut(`/api/compliance-types/${editingItem.id}`, {
          ...editingItem,
          type: newType.trim(),
        });
      } else {
        await apiPost("/api/compliance-types", {
          type: newType.trim(),
        });
      }
      setAddOpen(false);
      setEditingItem(null);
      load();
    } catch (err) {
      alert("Save failed: " + err.message);
    }
  }

  const columns = React.useMemo(
    () => [
      { name: "id", label: "ID", header: "ID", accessorKey: "id" },
      { name: "type", label: "Type", header: "Type", accessorKey: "type" },
    ],
    [],
  );

  return (
    <div className="h-[89.8vh]">
      <Routes>
        <Route
          index
          element={
            <Box sx={{ m: 1 }}>
              <Card>
                <CardContent>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6">Compliance Types</Typography>
                    <Button variant="contained" color="primary" onClick={openAddDialog}>
                      Add Compliance Type
                    </Button>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <DataTable
                      columns={columns}
                      rows={list}
                      onFiltersChange={handleFiltersChange}
                      extraControls={null}
                      onPageSizeChange={handlePageSizeChange}
                      totalCount={total}
                      currentPage={page}
                      onPageChange={handlePageChange}
                      onEdit={(_, row) => openEditDialog(row)}
                      onDelete={(_, row) => handleDelete(row)}
                    />
                    {loading ? <div style={{ padding: 12 }}>Loading...</div> : null}
                  </div>
                </CardContent>
              </Card>
            </Box>
          }
        />
      </Routes>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1 }}>
          {editingItem ? "Edit Compliance Type" : "Add Compliance Type"}
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 0" }}>
            <FormRow label="Type">
              <TextField
                size="small"
                margin="dense"
                fullWidth
                sx={fieldSx}
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                autoFocus
              />
            </FormRow>
          </div>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            {editingItem ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
